import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { getAllCampaigns } from '../services/campaignService';
import { seedCampaigns } from '../scripts/seedCampaigns';

interface ConnectionStatus {
  firestore: 'pending' | 'connected' | 'error';
  auth: 'pending' | 'connected' | 'error';
  campaigns: number;
  error?: string;
}

const FirebaseConnectionTest: React.FC = () => {
  const [status, setStatus] = useState<ConnectionStatus>({
    firestore: 'pending',
    auth: 'pending',
    campaigns: 0
  });
  const [isSeeding, setIsSeeding] = useState(false);

  useEffect(() => {
    testConnection();
  }, []);

  const testConnection = async () => {
    try {
      // Test Firestore connection
      console.log('Testing Firestore connection...');
      const testCollection = collection(db, 'campaigns');
      await getDocs(testCollection);
      
      // Count campaigns
      const campaigns = await getAllCampaigns();
      
      setStatus({
        firestore: 'connected',
        auth: auth ? 'connected' : 'error',
        campaigns: campaigns.length
      });
      
      console.log('✅ Firebase connected successfully!');
      console.log(`📊 Found ${campaigns.length} campaigns`);
      
    } catch (error) {
      console.error('❌ Firebase connection error:', error);
      setStatus({
        firestore: 'error',
        auth: 'error',
        campaigns: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  const handleSeedCampaigns = async () => {
    setIsSeeding(true);
    try {
      await seedCampaigns();
      alert('✅ Campaigns seeded successfully! Refresh to see updated count.');
      await testConnection(); // Refresh campaign count
    } catch (error) {
      console.error('Error seeding campaigns:', error);
      alert('❌ Error seeding campaigns. Check console for details.');
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '600px',
      margin: '40px auto',
      border: '2px solid #e0e0e0',
      borderRadius: '12px',
      backgroundColor: '#f9f9f9'
    }}>
      <h2 style={{ marginBottom: '20px', color: '#333' }}>
        🔥 Firebase Connection Test
      </h2>
      
      <div style={{ marginBottom: '15px' }}>
        <strong>Firestore Status:</strong>{' '}
        <span style={{
          color: status.firestore === 'connected' ? 'green' : 
                 status.firestore === 'error' ? 'red' : 'orange'
        }}>
          {status.firestore === 'connected' ? '✅ Connected' : 
           status.firestore === 'error' ? '❌ Error' : '⏳ Testing...'}
        </span>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <strong>Authentication Status:</strong>{' '}
        <span style={{
          color: status.auth === 'connected' ? 'green' : 
                 status.auth === 'error' ? 'red' : 'orange'
        }}>
          {status.auth === 'connected' ? '✅ Initialized' : 
           status.auth === 'error' ? '❌ Error' : '⏳ Testing...'}
        </span>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <strong>Campaigns in Database:</strong>{' '}
        <span style={{
          color: status.campaigns > 0 ? 'green' : 'orange',
          fontWeight: 'bold'
        }}>
          {status.campaigns}
        </span>
      </div>

      {status.error && (
        <div style={{
          padding: '10px',
          backgroundColor: '#ffebee',
          border: '1px solid #ef5350',
          borderRadius: '6px',
          marginTop: '15px',
          color: '#c62828'
        }}>
          <strong>Error:</strong> {status.error}
        </div>
      )}

      {status.campaigns === 0 && status.firestore === 'connected' && (
        <div style={{ marginTop: '20px' }}>
          <p style={{ color: '#666', marginBottom: '10px' }}>
            ⚠️ No campaigns found in database. Would you like to seed with demo data?
          </p>
          <button
            onClick={handleSeedCampaigns}
            disabled={isSeeding}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FA00FF',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              cursor: isSeeding ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '14px',
              opacity: isSeeding ? 0.6 : 1
            }}
          >
            {isSeeding ? '🌱 Seeding...' : '🌱 Seed 4 Demo Campaigns'}
          </button>
        </div>
      )}

      <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
        <button
          onClick={testConnection}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          🔄 Refresh Connection Test
        </button>
      </div>
    </div>
  );
};

export default FirebaseConnectionTest;

